import React from "react";

import { useTranslation } from "react-i18next";

import { SegmentedFieldBase } from "@/src/components/form-fields/SegmentedField";
import {
  applySimpleRestrictionPatch,
  getMediaRightList,
  getMediaRightsPayload,
  getSimpleRestrictionValue,
  setRightsMode,
  updateMediaRight,
} from "@/src/utils/mediaRights";

import RightsAdvancedAccordion from "./RightsAdvancedAccordion";
import RightsRestrictionFields from "./RightsRestrictionFields";

import type { JSX } from "react";

import type {
  GetMediaResponse,
  MediaDistributionType,
  MediaRight,
} from "@/src/api/MediaAPI.types";
import type { SimpleRestrictionValue } from "@/src/utils/mediaRights";

interface RightsRestrictionsBlockProps {
  item: GetMediaResponse;
  disabled?: boolean;
  visibleDistributionTypeList: MediaDistributionType[];
  onItemUpdate: (item: GetMediaResponse) => void;
}

export default function RightsRestrictionsBlock({
  item,
  disabled,
  visibleDistributionTypeList,
  onItemUpdate,
}: RightsRestrictionsBlockProps): JSX.Element {
  const { t } = useTranslation("editor-content");
  const isAdvanced = item.rights_mode === "advanced";
  const rightList = isAdvanced ? getMediaRightList(item) : [];

  const updateItem = (nextItem: GetMediaResponse): void => {
    onItemUpdate({ ...nextItem, rights: getMediaRightsPayload(nextItem) });
  };

  const handleModeChange = (mode: string): void => {
    updateItem(
      setRightsMode(item, mode === "advanced" ? "advanced" : "simple"),
    );
  };

  const handleRightChange = (
    distributionType: MediaDistributionType,
    patch: Partial<MediaRight>,
  ): void => {
    updateItem({
      ...item,
      rights: updateMediaRight(rightList, distributionType, patch),
    });
  };

  const handleSimpleRestrictionChange = (
    patch: Partial<SimpleRestrictionValue>,
  ): void => {
    updateItem(applySimpleRestrictionPatch(item, patch));
  };

  return (
    <div className="flex flex-col gap-8">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <SegmentedFieldBase
          ariaLabel={t("rightsMode")}
          disabled={disabled}
          onValueChange={handleModeChange}
          value={item.rights_mode}
          variant="primary"
          options={[
            { value: "simple", label: t("rightsModeSimple") },
            { value: "advanced", label: t("rightsModeAdvanced") },
          ]}
        />
        <p className="text-muted-foreground text-sm">
          {t(
            isAdvanced
              ? "rightsModeAdvancedDescription"
              : "rightsModeSimpleDescription",
          )}
        </p>
      </div>
      {isAdvanced ? (
        <RightsAdvancedAccordion
          disabled={disabled}
          onRightChange={handleRightChange}
          rightList={rightList}
          visibleDistributionTypeList={visibleDistributionTypeList}
        />
      ) : (
        <RightsRestrictionFields
          disabled={disabled}
          onChange={handleSimpleRestrictionChange}
          value={getSimpleRestrictionValue(item)}
        />
      )}
    </div>
  );
}
